import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { PageChrome, ROUTES, useIsDesktop } from '@/components/gg/chrome'
import { useAuthStore } from '@/store/auth'
import { useRecordsState, useSetRecords, useSetRecordType, useSetRecordSource, useAreaSearch, useRunRecordsCheck } from '@/hooks/useRecords'
import { RECORD_TYPES, type RecordSource, type RecordTypeId, type RecordsState } from '@/components/alerts/types'
import { longDay, useToast } from '@/components/alerts/ui'
import { TopicGlyph } from '@/components/gg/TopicGlyph'
import { useCoveredPlaces } from '@/hooks/useControls'
import '@/styles/alerts.css'

const STATUS: Record<RecordSource['status'], [string, string]> = {
  checking: ['ok', 'Checking weekly'], unreachable: ['bad', 'Couldn’t reach'], unavailable: ['na', 'Not available here'],
}
const short = (t: string | null) => (t ? new Date(t).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) : '')

/**
 * Public records (/app/feed/records). A feed control. Brian's
 * settings-records.html v1.1 and docs/PUBLIC-RECORDS.md v0.3.
 *
 * The records budget (its own, checked weekly early Monday; Sprout 4 · Grove
 * 12 · Harvest 30 sources; resets Monday; each source checked is 1, one
 * GoGrov can't reach is free; pause). What to look for (the record types; a
 * type on runs the free area search). Then a card per place followed in
 * Places with that place's sources, each its own switch at 1 seed a week;
 * county and state sources in a card that covers your places. No filters; the
 * never-a-find rules are fixed; sports parked.
 */
export function RecordsSettingsPage() {
  const desk = useIsDesktop()
  const { data: r } = useRecordsState()
  const toast = useToast()
  const search = useAreaSearch()
  const asked = useRef(false)

  // A place just followed shows its search running (free).
  const unsearched = r ? r.places.filter((p) => p.scope !== 'national' && !r.sources.some((s) => s.scope === 'place' && s.place.toLowerCase() === p.name.toLowerCase())) : []
  const localOn = r ? r.types.filter((t) => t !== 'release') : []
  useEffect(() => {
    if (!r || asked.current || search.isPending || !localOn.length || !unsearched.length) return
    asked.current = true
    search.mutate(undefined)
  }, [r, localOn.length, unsearched.length, search])

  return (
    <>
      <PageChrome title="Public records" navKey="records-settings" back={{ to: ROUTES.feedControls, label: 'Feed controls' }}
        actions={desk ? <Link className="gg-act" to={ROUTES.records}>Record finds</Link> : undefined} />
      <div className={`gg gga${desk ? ' gga--wide' : ''}`} style={desk ? { maxWidth: 820 } : undefined}>
        <p className="gga-lead">Every Monday GoGrov reads the permits, licenses and meeting agendas for your places and brings you anything worth a story, often before the news has it. Each source checked uses 1 seed, found or not.</p>
        {r && <Budget r={r} />}
        {r && <Kinds r={r} toast={toast.show} />}
        {r && <Places r={r} searching={search.isPending} unsearched={unsearched.map((p) => p.name)} />}
        <div className="gga-cardbox gga-parked">
          <div className="gga-row">
            <TopicGlyph topic="local-sports" size={30} />
            <div className="t"><div className="n">Sports schedules and scores</div><div className="s off">Not yet</div>
              <div className="p">Every state and sport publishes these differently, and there’s no open feed. Parked for now.</div></div>
          </div>
        </div>
        <div className="gga-never"><b>Never a find:</b> a private person’s name (applicants, owners, homeowners: business names only), court or criminal records, anything about a minor, renewals and routine paperwork.</div>
        {!desk && r && <Link className="gga-another" to={ROUTES.records} style={{ textDecoration: 'none' }}>Record finds</Link>}
        <DevTest />
      </div>
      {toast.node}
    </>
  )
}

function Budget({ r }: { r: RecordsState }) {
  const set = useSetRecords()
  const pct = Math.min(100, Math.round((r.spent / Math.max(1, r.cap)) * 100))
  return (
    <section className={`gga-cardbox${r.paused ? ' off' : ''}`}>
      <div className="gga-row">
        <div className="t"><div className="n">Records budget</div><div className={`s${r.paused ? ' off' : ''}`}>{r.paused ? 'Paused · nothing checked or charged' : 'Checked every Monday, early'}</div></div>
        <button className="gg-switch" type="button" role="switch" aria-checked={!r.paused} aria-label="Checking" onClick={() => set.mutate({ paused: !r.paused })} />
      </div>
      <div className="gga-bank">
        <div className="t">
          <div className="k">This week</div>
          <div className="v"><b>{r.spent}</b><small>of {r.cap} sources</small></div>
          <div className={`gga-meter${r.paused ? ' off' : ''}`}><i style={{ width: `${pct}%` }} /></div>
        </div>
      </div>
      <p className="gga-fine">Your plan checks up to <b>{r.cap} sources a week</b> (a count of sources; each check is 1 seed from your records budget, not your week). The count resets Monday. Next check: <b>{longDay(r.next_check)}</b>.{r.national ? ' Release days are checked the morning they land, from the same budget.' : ''}</p>
      {r.over_budget > 0 && <div className="gga-warn">{r.over_budget} {r.over_budget === 1 ? 'source is' : 'sources are'} over your budget and won’t be checked. Turn some off, or move up a plan.</div>}
      <div className="gga-hr" />
      <div className="gga-row">
        <div className="t"><div className="n" style={{ fontSize: 14 }}>Push notifications</div>
          <div className="p">{r.push ? 'On. “This week’s records: 3 finds.” One a week at most.' : 'Off. Finds wait in Record finds with no notification.'}</div></div>
        <button className="gg-switch" type="button" role="switch" aria-checked={r.push} aria-label="Records push" onClick={() => set.mutate({ push: !r.push })} />
      </div>
    </section>
  )
}

function Kinds({ r, toast }: { r: RecordsState; toast: (b: React.ReactNode) => void }) {
  const setType = useSetRecordType()
  const [busy, setBusy] = useState<RecordTypeId | null>(null)
  const order: RecordTypeId[] = [...(r.local || !r.national ? (['liquor', 'business', 'permit', 'agenda'] as const) : []), ...(r.national ? (['release'] as const) : [])]
  return (
    <>
      <div className="gga-sect">What to look for</div>
      <section className="gga-cardbox">
        {order.map((k) => {
          const t = RECORD_TYPES[k], on = r.types.includes(k)
          return (
            <div className={`gga-kind${on ? '' : ' off'}`} key={k}>
              <TopicGlyph topic={t.topic} size={26} />
              <div className="t"><div className="n">{t.name}</div><div className="p">{busy === k ? <span className="gga-searching">Searching your places’ public sources. The search is free.</span> : t.desc}</div></div>
              <button className="gg-switch" type="button" role="switch" aria-checked={on} aria-label={t.name} disabled={busy === k}
                onClick={() => { setBusy(k); setType.mutate({ type: k, on: !on }, { onSettled: () => setBusy(null), onError: (e) => toast((e as Error).message) }) }} />
            </div>
          )
        })}
      </section>
    </>
  )
}

function SourceRow({ s }: { s: RecordSource }) {
  const set = useSetRecordSource()
  const [cls, lab] = STATUS[s.status]
  const T = RECORD_TYPES[s.type]
  const label = s.on || s.status === 'unavailable' ? lab : 'Off'
  return (
    <div className="gga-sr">
      <div className="t">
        <div className="nm">{s.url ? <a href={s.url} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit' }}>{s.name}</a> : s.name}</div>
        <div className="pl">{T?.name ?? s.type}{s.last_checked_at ? ` · checked ${short(s.last_checked_at)}` : s.last_tried_at ? ` · tried ${short(s.last_tried_at)}` : ''}{s.type === 'release' && s.next_release ? ` · next ${short(s.next_release + 'T12:00:00')}` : ''}</div>
        <span className={`gga-st ${label === 'Off' ? 'off' : cls}`}>{label}</span>
      </div>
      {s.status !== 'unavailable' && <button className="gg-switch" type="button" role="switch" aria-checked={s.on} aria-label={s.name} onClick={() => set.mutate({ id: s.id, on: !s.on })} />}
    </div>
  )
}

function PlaceCard({ name, sub, list, note }: { name: string; sub: string; list: RecordSource[]; note: React.ReactNode }) {
  const n = list.filter((x) => x.on && x.status === 'checking').length
  return (
    <section className="gga-cardbox gga-place">
      <div className="gga-row">
        <div className="t"><div className="n">{name}</div><div className="s off">{sub}</div></div>
        <span className="cnt">{n ? `${n} ${n === 1 ? 'seed' : 'seeds'} a week` : 'Nothing checked'}</span>
      </div>
      <div className="gga-srcs">{list.length ? list.map((s) => <SourceRow key={s.id} s={s} />) : <div className="gga-sr"><div className="t"><div className="nm">{note}</div></div></div>}</div>
    </section>
  )
}

function Places({ r, searching, unsearched }: { r: RecordsState; searching: boolean; unsearched: string[] }) {
  const { data: covered } = useCoveredPlaces()
  const regionOf = (name: string) => (covered ?? []).find((c) => c.name.toLowerCase() === name.toLowerCase())?.region
  const live = r.sources.filter((x) => r.types.includes(x.type))
  if (r.national && !r.local) {
    return (
      <>
        <div className="gga-sect">National releases</div>
        <PlaceCard name="National releases" sub="Checked the morning each lands" list={live.filter((x) => x.type === 'release')} note="No release turned on." />
      </>
    )
  }
  const followed = r.places.filter((p) => p.scope !== 'national')
  const wide = live.filter((x) => x.scope !== 'place' && x.type !== 'release')
  const wideNames = [...new Set(wide.map((x) => x.place))]
  const releases = live.filter((x) => x.type === 'release')
  return (
    <>
      <div className="gga-sect"><span>Your places</span><Link to={ROUTES.places}>Change places</Link></div>
      <p className="gga-fine" style={{ margin: '0 0 10px' }}>GoGrov checks the places you follow. Each source that’s on is <b>1 seed a week</b> from your records budget; turn one off to save it. Add or remove places in <Link to={ROUTES.places}>Places</Link>.</p>
      {followed.map((p) => {
        const list = live.filter((x) => x.scope === 'place' && x.place.toLowerCase() === p.name.toLowerCase())
        const pending = unsearched.includes(p.name)
        return (
          <PlaceCard key={p.name} name={p.name} sub={p.home ? 'Your home place' : (regionOf(p.name) || 'A place you follow')} list={list}
            note={!r.types.some((t) => t !== 'release') ? 'Turn on a record type above to look for its sources here.'
              : pending ? <span className="gga-searching">{searching ? `Searching ${p.name}’s public sources. What GoGrov finds shows up here before Monday’s check. The search is free.` : `${p.name}’s sources will be searched (free).`}</span>
              : 'Nothing to check here for the record types you have on.'} />
        )
      })}
      {wideNames.map((pl) => <PlaceCard key={pl} name={pl} sub="Covers your places" list={wide.filter((x) => x.place === pl)} note="" />)}
      {releases.length > 0 && <PlaceCard name="National releases" sub="Checked the morning each lands" list={releases} note="" />}
    </>
  )
}

/** Brian's "Run the records check" test button, for admins and local development. */
function DevTest() {
  const isAdmin = useAuthStore((s) => s.isAdmin)
  const run = useRunRecordsCheck()
  const [msg, setMsg] = useState('Monday’s check, now: 1 from the records budget for each source checked; nothing for one it can’t reach.')
  if (!isAdmin && !import.meta.env.DEV) return null
  return (
    <div className="gga-dev">
      <div className="k">Public records · test</div>
      <div className="row">
        <button type="button" className="gga-btn" disabled={run.isPending} onClick={() => { setMsg('Checking your sources. This takes a minute or two.'); run.mutate(undefined, {
          onSuccess: (x) => {
            const d = x as { why?: string; checked?: number; charged?: number; found?: number; unreachable?: number }
            setMsg(d.why ? `Nothing checked: ${d.why}.` : `Checked ${d.checked} ${d.checked === 1 ? 'source' : 'sources'} (${d.charged} from the records budget): ${d.found} new.${d.unreachable ? ` ${d.unreachable} couldn’t be reached, no seed.` : ''}`)
          },
          onError: (e) => setMsg((e as Error).message),
        }) }}>Run the records check</button>
      </div>
      <p>{msg}</p>
    </div>
  )
}
